import { useEffect, useState } from 'react';
import { UserX } from 'lucide-react';
import { PlayerProfile } from '../PlayerProfile';
import { usersApiService } from '../../services/usersApi';
import { ShopChangelogUserIdType } from '../../types/shopChangelog';

interface ResolvedDiscord {
  status: 'resolved' | 'not_found' | 'error' | 'loading';
  uuid?: string;
  username?: string;
}

// Module-scoped cache so a feed page with the same Discord user repeated across
// many rows triggers a single lookup. Keys are Discord snowflakes.
const discordCache = new Map<string, ResolvedDiscord>();
const inflight = new Map<string, Promise<ResolvedDiscord>>();

function resolveDiscord(discordId: string): Promise<ResolvedDiscord> {
  const cached = discordCache.get(discordId);
  if (cached && cached.status !== 'loading') return Promise.resolve(cached);
  const existing = inflight.get(discordId);
  if (existing) return existing;

  const p = usersApiService
    .getPlayerStatsByDiscordId(discordId)
    .then((res): ResolvedDiscord => {
      if (res?.player_stats?.uuid) {
        const r: ResolvedDiscord = {
          status: 'resolved',
          uuid: res.player_stats.uuid,
          username: res.player_stats.username,
        };
        discordCache.set(discordId, r);
        return r;
      }
      const r: ResolvedDiscord = { status: 'not_found' };
      discordCache.set(discordId, r);
      return r;
    })
    .catch((): ResolvedDiscord => {
      const r: ResolvedDiscord = { status: 'error' };
      discordCache.set(discordId, r);
      return r;
    })
    .finally(() => {
      inflight.delete(discordId);
    });

  inflight.set(discordId, p);
  return p;
}

interface ShopChangelogUserProps {
  userId: string | null;
  userIdType: ShopChangelogUserIdType;
  inline?: boolean;
  showUuid?: boolean;
  showAvatar?: boolean;
}

/**
 * Per-row user widget. Selection is driven by the explicit `userIdType` field
 * (no heuristics):
 *   - null      → "Unknown user"
 *   - 'uuid'    → <PlayerProfile uuid={userId} />
 *   - 'discord' → resolves snowflake to MC profile via the Users API and renders
 *                 a <PlayerProfile>; on failure renders a muted Discord pill.
 */
export function ShopChangelogUser({
  userId,
  userIdType,
  inline = false,
  showUuid = false,
  showAvatar = true,
}: ShopChangelogUserProps) {
  if (!userId) {
    return (
      <span className="inline-flex items-center gap-1 text-glass-400 italic">
        <UserX className="w-4 h-4" />
        Unknown user
      </span>
    );
  }

  if (userIdType === 'uuid') {
    return <PlayerProfile uuid={userId} inline={inline} showUuid={showUuid} showAvatar={showAvatar} />;
  }

  return <DiscordUserResolver discordId={userId} inline={inline} showUuid={showUuid} showAvatar={showAvatar} />;
}

interface DiscordUserResolverProps {
  discordId: string;
  inline: boolean;
  showUuid: boolean;
  showAvatar: boolean;
}

function DiscordUserResolver({ discordId, inline, showUuid, showAvatar }: DiscordUserResolverProps) {
  const [state, setState] = useState<ResolvedDiscord>(() => discordCache.get(discordId) ?? { status: 'loading' });

  useEffect(() => {
    if (state.status !== 'loading') return;
    let cancelled = false;
    resolveDiscord(discordId).then((r) => {
      if (!cancelled) setState(r);
    });
    return () => {
      cancelled = true;
    };
  }, [discordId, state.status]);

  if (state.status === 'resolved' && state.uuid) {
    return (
      <PlayerProfile
        uuid={state.uuid}
        name={state.username}
        inline={inline}
        showUuid={showUuid}
        showAvatar={showAvatar}
      />
    );
  }

  if (state.status === 'loading') {
    return <span className="text-xs text-glass-400 animate-pulse">Loading user…</span>;
  }

  // not_found / error → muted discord pill
  const shortId = discordId.length > 8 ? `${discordId.slice(0, 6)}…` : discordId;
  return (
    <span
      className="inline-flex items-center gap-1 font-semibold text-glass-300"
      title={`Discord ID: ${discordId}`}
    >
      <span>Discord user · </span>
      <span className="font-mono text-xs">{shortId}</span>
    </span>
  );
}
